import { useField, useFormikContext } from 'formik'
import { Configure, InstantSearch, Hits } from 'react-instantsearch'

import type { Hit, BaseHit } from 'instantsearch.js'
import type { ReactElement } from 'react'
import type { ITranslationOption } from '~/types/option'
import type { IPageContainerFormValues, IPlaceholderRule } from '~/types/template'
import type { ITranslationKeyValue } from '~/types/translation'

import CustomHit from './CustomHit'

import AlgoliaNoResultsBoundary from '~/components/Algolia/NoResultsBoundary'
import AlgoliaPagination from '~/components/Algolia/Pagination'
import AlgoliaSearch from '~/components/Algolia/Search'
import { ModalBody, ModalHeader } from '~/components/Modal/Layout'
import { ALGOLIA_INDEX_NAMES } from '~/const/algolia'
import { getSearchClient, getTranslationFilters } from '~/utils/algolia'

import './styles.scss'

const searchClient = getSearchClient()

interface Props {
  name: string
  onItemClick: VoidFunction
  rules?: IPlaceholderRule[]
}

const TranslationInputModal = ({ name, onItemClick, rules }: Props): ReactElement => {
  const { values } = useFormikContext<IPageContainerFormValues>()

  const [, { value }, { setValue }] = useField<ITranslationOption>(name)

  const configurationFilters =
    values.selectedApp &&
    getTranslationFilters({
      deviceType: values.selectedApp?.device_type,
      lokaliseId: values.selectedApp?.lokalise_id,
    })

  const handleItemClick = (option: ITranslationOption) => {
    setValue(option)
    onItemClick()
  }

  const transformItems = (items: (Hit<BaseHit> & ITranslationKeyValue)[]) => {
    return items.map((item) => ({
      ...item,
      onClick: () =>
        handleItemClick({
          label: item.key,
          params: [],
          text: item.value,
          translation: item,
          value: item.key,
        }),
      rules,
      translation: item,
    }))
  }

  return (
    <InstantSearch
      indexName={ALGOLIA_INDEX_NAMES.TRANSLATIONS}
      initialUiState={{
        [ALGOLIA_INDEX_NAMES.TRANSLATIONS]: {
          query: value.value,
        },
      }}
      searchClient={searchClient}
    >
      <div className="translation-input-modal">
        <ModalHeader className="translation-input-modal__header">
          <div className="search-container">
            <AlgoliaSearch
              defaultValue={value.value}
              label="Search a translation"
              placeholder="Search"
              wrapperClassName="search-container__input"
            />
          </div>
        </ModalHeader>
        <ModalBody className="translation-input-modal__body">
          <Configure filters={configurationFilters} hitsPerPage={10} />
          <AlgoliaNoResultsBoundary fallback={<span className="no-translations-message">No translations found.</span>}>
            <>
              <Hits hitComponent={CustomHit} transformItems={transformItems} />
              <AlgoliaPagination />
            </>
          </AlgoliaNoResultsBoundary>
        </ModalBody>
      </div>
    </InstantSearch>
  )
}

export default TranslationInputModal
